import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { canEnterApp } from '@helios/authz';
import { useAuth } from '@helios/auth-firebase-web';
import { useSendWhatsAppFreeForm, type FreeFormSourceType } from '../hooks/useSendWhatsAppFreeForm';

/* ------------------------------------------------------------------ */
/*  SendWhatsAppFreeFormAction (PRD.235)                              */
/*                                                                    */
/*  Appointment-context action for sending a custom WhatsApp message  */
/*  inside the 24-hour customer service window — typically "running  */
/*  late" or follow-up notes after a patient has confirmed.           */
/*                                                                    */
/*  - Role-gated: hidden for users without send permission.           */
/*  - Window-gated: server enforces the 24h rule; the UI shows the    */
/*    status (open / expired / never opened in) using a dry-run       */
/*    preflight to the same endpoint.                                  */
/*  - Templated quick-pick presets + free textarea for custom text.   */
/* ------------------------------------------------------------------ */

const ALLOWED_ROLES = ['admin', 'editor'] as const;

const PRESETS: ReadonlyArray<{
  id: 'running_late' | 'directions' | 'parking';
  label: string;
  sourceType: FreeFormSourceType;
  buildMessage: (input: PresetContext) => string;
}> = [
  {
    id: 'running_late',
    label: "I'm running late (15 min)",
    sourceType: 'running_late',
    buildMessage: ({ patientName }) =>
      `Hi ${patientName || 'there'} — quick heads up that we're running about 15 minutes behind schedule today. Sorry for the inconvenience, see you soon!`,
  },
  {
    id: 'directions',
    label: 'Send directions to the clinic',
    sourceType: 'directions',
    buildMessage: ({ patientName }) =>
      `Hi ${patientName || 'there'} — here are quick directions to the clinic. Let us know if you need anything else.`,
  },
  {
    id: 'parking',
    label: 'Parking info',
    sourceType: 'parking',
    buildMessage: ({ patientName }) =>
      `Hi ${patientName || 'there'} — parking is available at the building's ground floor; please bring your ticket to reception for validation.`,
  },
];

interface PresetContext {
  patientName: string;
}

export interface SendWhatsAppFreeFormActionProps {
  /** Appointment context — controls the audit reference. */
  appointment: {
    id: string;
    patientId: string;
    patientName: string;
  };
  /** Patient's mobile number for display in the confirm dialog. */
  patientPhone: string | null;
  /** Whether the patient has an explicit WhatsApp opt-in. */
  whatsAppOptedIn: boolean;
  /** Presentation mode (matches SendWhatsAppReminderAction). */
  presentation?: 'menu-item' | 'button';
  /** Called when the confirm dialog opens. */
  onConfirmOpen?: () => void;
}

export function SendWhatsAppFreeFormAction({
  appointment,
  patientPhone,
  whatsAppOptedIn,
  presentation = 'button',
  onConfirmOpen,
}: SendWhatsAppFreeFormActionProps) {
  const { claims } = useAuth();
  const { enabled, status, error, eligibility, checkEligibility, send, reset } =
    useSendWhatsAppFreeForm();

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sourceType, setSourceType] = useState<FreeFormSourceType>('custom');
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const canSend = canEnterApp(claims, {
    appId: 'whatsapp-freeform',
    entryAnyOfRoles: [...ALLOWED_ROLES],
  });

  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    [],
  );

  // Run the eligibility preflight on mount + whenever the patient or opt-in
  // changes — but only when the action can actually be used.
  useEffect(() => {
    if (!enabled || !canSend || !whatsAppOptedIn) return;
    void checkEligibility(appointment.patientId);
  }, [enabled, canSend, whatsAppOptedIn, appointment.patientId, checkEligibility]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  }, []);

  const trimmedMessage = message.trim();

  // Only hard blockers gate the button. The 24-hour eligibility window is
  // shown as an in-dialog warning pill instead — if the actual send fails
  // because the window has closed, the server error surfaces via `error`.
  // (Previously, eligibility.allowed === false disabled the button, which
  // left users stuck if the preflight stalled or the window had just opened.)
  const blockReason: string | null = !enabled
    ? 'WhatsApp messaging is not configured for this clinic'
    : !patientPhone
      ? 'No mobile number on file for this patient'
      : !whatsAppOptedIn
        ? 'Patient has not opted in to WhatsApp messaging'
        : null;

  const disabled = blockReason !== null;

  const openDialog = useCallback(() => {
    if (disabled) return;
    reset();
    setMessage('');
    setSourceType('custom');
    setSelectedPresetId(null);
    setOpen(true);
    onConfirmOpen?.();
    // Refresh eligibility on open in case the user's been on this screen for a while.
    void checkEligibility(appointment.patientId);
  }, [disabled, reset, onConfirmOpen, checkEligibility, appointment.patientId]);

  const applyPreset = useCallback(
    (presetId: string) => {
      const preset = PRESETS.find((p) => p.id === presetId);
      if (!preset) return;
      setSelectedPresetId(preset.id);
      setSourceType(preset.sourceType);
      setMessage(preset.buildMessage({ patientName: appointment.patientName }));
    },
    [appointment.patientName],
  );

  const handleSend = useCallback(async () => {
    if (status === 'sending') return; // double-click guard
    if (!trimmedMessage) return;
    const result = await send({
      patientId: appointment.patientId,
      message: trimmedMessage,
      sourceType,
      appointmentId: appointment.id,
      clientReference: `appointment:${appointment.id}:whatsapp-freeform:${sourceType}:${Date.now()}`,
    });
    if (result) {
      setOpen(false);
      showToast(
        result.accepted === false
          ? 'Message queued — provider did not accept it yet.'
          : `WhatsApp message sent to ${appointment.patientName}.`,
      );
    }
  }, [status, trimmedMessage, send, appointment, sourceType, showToast]);

  if (!canSend) return null;

  const statusLine = renderStatusLine({
    eligibilityLoading: eligibility.loading,
    eligibilityAllowed: eligibility.allowed,
    eligibilityMessage: eligibility.message,
    windowExpiresAt: eligibility.windowExpiresAt,
    blockReason,
  });

  return (
    <>
      {presentation === 'button' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <Button
            variant="outline"
            size="md"
            onClick={openDialog}
            disabled={disabled}
            title={blockReason ?? undefined}
          >
            Send a custom message
          </Button>
          {statusLine && (
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              {statusLine}
            </span>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={openDialog}
          disabled={disabled}
          title={blockReason ?? undefined}
          style={{
            ...menuItemStyle,
            color: disabled ? 'var(--text-muted)' : 'var(--text)',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <span>Send a custom message</span>
          {statusLine && (
            <span
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                marginTop: 2,
              }}
            >
              {statusLine}
            </span>
          )}
        </button>
      )}

      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Send a custom WhatsApp message"
        description={`Free-form messages are allowed only while the 24-hour reply window is open.`}
        maxWidth={480}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <Button variant="ghost" size="md" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="gradient"
              size="md"
              onClick={handleSend}
              disabled={status === 'sending' || !trimmedMessage}
            >
              {status === 'sending' ? 'Sending…' : 'Send Message'}
            </Button>
          </div>
        }
      >
        <div style={modalBodyStyle}>
          <SummaryBox patientName={appointment.patientName} whatsAppNumber={patientPhone ?? '—'} />

          <WindowStatusPill eligibility={eligibility} />

          <div>
            <label style={fieldLabelStyle}>Quick presets</label>
            <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
              {PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPreset(preset.id)}
                  style={presetButtonStyle(selectedPresetId === preset.id)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="wa-freeform-msg" style={fieldLabelStyle}>
              Message
            </label>
            <textarea
              id="wa-freeform-msg"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                // If user edits a preset, treat it as a custom message.
                if (selectedPresetId) {
                  setSelectedPresetId(null);
                  setSourceType('custom');
                }
              }}
              rows={5}
              maxLength={1024}
              placeholder="Type a short message to the patient…"
              style={textareaStyle}
            />
            <div
              style={{
                marginTop: 4,
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                textAlign: 'right',
              }}
            >
              {trimmedMessage.length} / 1024
            </div>
          </div>

          {status === 'error' && error && <div style={errorBoxStyle}>{error}</div>}
        </div>
      </Modal>

      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: 'var(--sp-6)',
            right: 'var(--sp-6)',
            zIndex: 1000,
            padding: 'var(--sp-3) var(--sp-4)',
            borderRadius: 'var(--radius-md)',
            background: '#dcfce7',
            color: '#15803d',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast}
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Window status display                                             */
/* ------------------------------------------------------------------ */

function WindowStatusPill({
  eligibility,
}: {
  eligibility: ReturnType<typeof useSendWhatsAppFreeForm>['eligibility'];
}) {
  const display = useMemo(() => buildWindowDisplay(eligibility), [eligibility]);
  return (
    <div
      style={{
        padding: 'var(--sp-3) var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        background: display.bg,
        border: `1px solid ${display.border}`,
        color: display.fg,
        fontSize: 'var(--font-size-sm)',
        lineHeight: 'var(--line-height-base)',
      }}
    >
      <div style={{ fontWeight: 'var(--font-weight-semi)' }}>{display.title}</div>
      <div style={{ fontSize: 'var(--font-size-xs)', marginTop: 2 }}>{display.detail}</div>
    </div>
  );
}

function buildWindowDisplay(
  eligibility: ReturnType<typeof useSendWhatsAppFreeForm>['eligibility'],
): { title: string; detail: string; bg: string; border: string; fg: string } {
  if (eligibility.loading) {
    return {
      title: 'Checking 24-hour window…',
      detail: ' ',
      bg: '#f1f5f9',
      border: '#e2e8f0',
      fg: '#475569',
    };
  }
  if (eligibility.allowed === true) {
    const expires = eligibility.windowExpiresAt
      ? new Date(eligibility.windowExpiresAt).toLocaleString()
      : null;
    return {
      title: '24-hour window is open',
      detail: expires ? `Free-form sending available until ${expires}.` : ' ',
      bg: '#dcfce7',
      border: '#bbf7d0',
      fg: '#15803d',
    };
  }
  if (eligibility.allowed === false) {
    return {
      title: 'Free-form messaging is blocked',
      detail:
        eligibility.message ??
        'Use an approved template (e.g. APPOINTMENT_REMINDER) until the patient messages us.',
      bg: '#fef3c7',
      border: '#fde68a',
      fg: '#92400e',
    };
  }
  return {
    title: 'Window status unavailable',
    detail: 'Try again or use an approved template.',
    bg: '#f1f5f9',
    border: '#e2e8f0',
    fg: '#475569',
  };
}

function renderStatusLine(input: {
  eligibilityLoading: boolean;
  eligibilityAllowed: boolean | null;
  eligibilityMessage: string | null;
  windowExpiresAt: string | null;
  blockReason: string | null;
}): string | null {
  if (input.blockReason) return input.blockReason;
  if (input.eligibilityLoading) return 'Checking 24-hour window…';
  if (input.eligibilityAllowed === true && input.windowExpiresAt) {
    return `Window open until ${new Date(input.windowExpiresAt).toLocaleString()}`;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/*  Confirm-dialog summary block                                      */
/* ------------------------------------------------------------------ */

function SummaryBox({
  patientName,
  whatsAppNumber,
}: {
  patientName: string;
  whatsAppNumber: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
        padding: 'var(--sp-3) var(--sp-4)',
        background: 'var(--bg-surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <SummaryRow label="Patient" value={patientName} />
      <SummaryRow label="WhatsApp" value={whatsAppNumber} />
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-4)' }}>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>{label}</span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text)',
          textAlign: 'right',
        }}
      >
        {value}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Styles                                                            */
/* ------------------------------------------------------------------ */

const modalBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-4)',
  padding: 'var(--sp-5, 20px) var(--sp-6, 24px)',
};

const fieldLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'var(--text-muted)',
  marginBottom: 'var(--sp-2)',
};

const textareaStyle: CSSProperties = {
  width: '100%',
  padding: 'var(--sp-3) var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-base)',
  color: 'var(--input-text)',
  fontFamily: 'inherit',
  resize: 'vertical',
  boxSizing: 'border-box',
};

const errorBoxStyle: CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  borderRadius: 'var(--radius-md)',
  background: '#fef2f2',
  color: '#dc2626',
  fontSize: 'var(--font-size-sm)',
};

function presetButtonStyle(selected: boolean): CSSProperties {
  return {
    padding: '6px 12px',
    borderRadius: 'var(--radius-full)',
    border: `1px solid ${selected ? 'var(--color-primary, #2563eb)' : 'var(--border)'}`,
    background: selected ? 'rgba(37, 99, 235, 0.08)' : 'var(--bg-surface)',
    color: selected ? 'var(--color-primary, #2563eb)' : 'var(--text)',
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
}

const menuItemStyle: CSSProperties = {
  width: '100%',
  padding: 'var(--sp-2) var(--sp-3)',
  background: 'none',
  border: 'none',
  textAlign: 'left',
  fontSize: 'var(--font-size-sm)',
};
